import type { AsyncThunkApiConfig } from '@/store'

import { createAsyncThunk } from '@reduxjs/toolkit'

import { convertToClientUserProfile } from '@/local/server-data/domain/server/factories/user'
import { replaceDomainData } from '@/features/domain'
import { loginService } from '@/services'

import { loadInitialData } from './loadInitialData'

type LoginData = Parameters<typeof loginService>[0] & {
  territoryId?: string
  simulationId?: string
}

export type LoginError =
  // for all the below errors, the http statusCode is 401
  | { errorCode: 100; errorMessage: 'LOGIN_ERROR_NO_USER' }
  | { errorCode: 103; errorMessage: 'LOGIN_ERROR_ACCOUNT_SUSPENDED' }
  | { errorCode: 104; errorMessage: 'LOGIN_ERROR_ACCOUNT_EXPIRED' }
  | { errorCode: 105; errorMessage: 'LOGIN_ERROR_ACCOUNT_INACTIVE' }
  | { errorCode: 109; errorMessage: 'LOGIN_ERROR_USER_FILTERED_GUEST_NO_COMPANIES' }
  | { errorCode: 110; errorMessage: 'LOGIN_ERROR_USER_NO_TERRITORIES' }
  | { errorCode: 170; errorMessage: 'LOGIN_ERROR_USER_DISABLED' }
  | { errorCode: 191; errorMessage: 'LOGIN_ERROR_PASSWORD_MATCHES_USERNAME' }
  | { errorCode: 403; errorMessage: 'UNAUTHORIZED_USER_NOT_ADMIN_OR_PLANNER' }
  | { errorCode: 404; errorMessage: 'UNAUTHORIZED_ACCOUNT' }

/**
 * Password-throttling state carried on a 401 body (RO-2829).
 *
 * The server owns the counting and the lock; the front end only renders the
 * phase this picks out. `unlockAtDisplay` arrives already formatted in the
 * account's saved time format and must be rendered untouched — the preference
 * lives on the profile, which only loads after login.
 */
export type LoginThrottleFailure =
  | { errorCode: 100 }
  | { errorCode: 112; attemptsRemaining: number }
  | { errorCode: 111; lockedUntilEpochMs: number; unlockAtDisplay?: string }

type RejectErrorType =
  | LoginError['errorMessage']
  | 'invalid-login'
  | 'proxy-error'
  | 'too-many-sessions'
  | 'unknown'

export type RejectError = {
  message: string
  error: Error
  type: RejectErrorType
  /** set when the 401 carried password-throttling state */
  throttle?: LoginThrottleFailure
}

/**
 * How many consecutive failures the server allows before it locks the account.
 *
 * Duplicated from the server: the 401 body reports `failedCount`, not attempts
 * left, so the warning counter has to be worked out here. Retuning the limit
 * server-side without updating this constant would make the counter lie.
 */
const MAX_ATTEMPTS_BEFORE_LOCK = 5

/**
 * Reads the throttling state off a 401 body, if it carries any.
 *
 * Parsed defensively from `unknown` rather than through `LoginError`: the
 * throttling codes are not part of that union, their `errorMessage` values are
 * the server's to define, and everything under `errorDetails` arrives as a
 * string.
 */
function parseThrottleFailure(body: unknown): LoginThrottleFailure | undefined {
  if (typeof body !== 'object' || body === null) return undefined

  const { errorCode, errorDetails } = body as { errorCode?: unknown; errorDetails?: unknown }

  switch (errorCode) {
    case 112: {
      const failedCount = numberFrom(errorDetails, 'failedCount')

      // without a usable count there is nothing to count down, so fall back to
      // the plain error banner rather than rendering "NaN attempts remaining"
      if (failedCount === undefined) return { errorCode: 100 }

      const attemptsRemaining = MAX_ATTEMPTS_BEFORE_LOCK - failedCount

      // a non-positive count means our copy of the server's limit has drifted —
      // show the plain error banner rather than "0 attempts remaining"
      if (attemptsRemaining <= 0) return { errorCode: 100 }

      return { errorCode: 112, attemptsRemaining }
    }

    case 111: {
      const lockedUntilEpochMs = lockDeadlineFrom(errorDetails)

      // an expired or unreadable lock leaves nothing to count down, so show the
      // plain error banner rather than a form the user cannot use
      if (lockedUntilEpochMs === undefined) return { errorCode: 100 }

      // On the longer locks the server sends the unlock time already formatted
      // in the account's own time format, and its presence is what swaps the
      // ticking countdown for a fixed time. Passed straight through: the front
      // end must not reformat it, and cannot — the time-format preference
      // lives on the profile, which only loads after login.
      const unlockAtDisplay = stringFrom(errorDetails, 'unlockAtDisplay')

      return { errorCode: 111, lockedUntilEpochMs, unlockAtDisplay }
    }

    case 100:
      return { errorCode: 100 }

    default:
      return undefined
  }
}

/**
 * When the lock ends, in epoch ms.
 *
 * The body carries both an absolute deadline and a relative one, and only the
 * live lock populates its field — the other reads `0`. Take the absolute value
 * when it is set, otherwise derive it from the remaining time.
 */
function lockDeadlineFrom(errorDetails: unknown): number | undefined {
  const lockedUntilEpochMs = numberFrom(errorDetails, 'lockedUntilEpochMs')
  if (lockedUntilEpochMs !== undefined && lockedUntilEpochMs > Date.now()) {
    return lockedUntilEpochMs
  }

  const remainingMs = numberFrom(errorDetails, 'remainingMs')
  if (remainingMs !== undefined && remainingMs > 0) {
    return Date.now() + remainingMs
  }

  return undefined
}

/** Reads a non-empty `errorDetails` string, or `undefined` when absent. */
function stringFrom(errorDetails: unknown, field: string): string | undefined {
  if (typeof errorDetails !== 'object' || errorDetails === null) return undefined

  const raw = (errorDetails as Record<string, unknown>)[field]

  return typeof raw === 'string' && raw.length > 0 ? raw : undefined
}

/** `errorDetails` values come over as strings, so every read goes through this. */
function numberFrom(errorDetails: unknown, field: string): number | undefined {
  if (typeof errorDetails !== 'object' || errorDetails === null) return undefined

  const raw = (errorDetails as Record<string, unknown>)[field]
  if (typeof raw !== 'string' && typeof raw !== 'number') return undefined

  const value = Number(raw)
  return Number.isFinite(value) ? value : undefined
}

export const login = createAsyncThunk<
  // Return type of the payload creator
  uui.domain.client.UserProfile,
  // First argument to the payload creator
  LoginData,
  // thunk API
  AsyncThunkApiConfig<RejectError>
>('lifecycle/login', async (data, thunkApi) => {
  let userProfile: uui.domain.server.UserProfile | undefined

  try {
    const { password, username, serverUrl } = data
    thunkApi.extra.journal.store(`Trying to log as: ${username}`)

    userProfile = await loginService({ password, username, serverUrl })
  } catch (e) {
    const errorData = e.response.data as LoginError
    const { errorMessage: message } = errorData

    thunkApi.extra.journal.store(message, { tags: ['login', 'manual-login'] }, 'warn')

    return thunkApi.rejectWithValue({
      message,
      error: { name: 'invalid-login', message },
      type: message,
      throttle: parseThrottleFailure(e.response.data),
    })
  }

  if (!userProfile) {
    const message = 'Invalid login credentials'
    thunkApi.extra.journal.store(message, { tags: ['login', 'manual-login'] }, 'warn')
    return thunkApi.rejectWithValue({
      message,
      error: { name: 'invalid-login', message },
      type: 'invalid-login',
      throttle: { errorCode: 100 },
    })
  }

  try {
    const clientUserProfile = convertToClientUserProfile(userProfile)

    // initialize server-data web-worker,
    const initialDomainState = await thunkApi.extra.domainProxy.create(
      data.serverUrl,
      clientUserProfile,
    )

    if (!initialDomainState || initialDomainState === 'tooManySessionsError') {
      let message = ''
      let name: RejectErrorType

      switch (initialDomainState) {
        case false:
          message = 'The Domain WebWorker failed to connect (Unknown error).'
          name = 'proxy-error'
          break
        case 'tooManySessionsError':
          message = 'The Domain WebWorker failed to connect (Too many open sessions).'
          name = 'too-many-sessions'
          break
        default:
          message = 'The Domain WebWorker failed to connect (Too many open sessions).'
          name = 'unknown'
          break
      }

      thunkApi.extra.journal.store(message, { tags: ['login', 'auto-login'] }, 'error')

      return thunkApi.rejectWithValue({
        error: { name, message },
        type: name,
        message,
      })
    }

    thunkApi.dispatch(replaceDomainData(initialDomainState))

    const territoryId =
      data.territoryId ||
      (clientUserProfile.user.type !== 'gpsonly'
        ? clientUserProfile.user.uiData.lastOpenedSession?.territoryId
        : undefined)

    const simulationId =
      data.simulationId ||
      (clientUserProfile.user.type !== 'gpsonly'
        ? clientUserProfile.user.uiData.lastOpenedSession?.planId !== 'ops'
          ? clientUserProfile.user.uiData.lastOpenedSession?.planId
          : undefined
        : undefined)

    thunkApi.dispatch(loadInitialData({ simulationId, territoryId }))

    // return the profile
    return clientUserProfile
  } catch (error) {
    const message = 'Login Failed'
    thunkApi.extra.journal.store(message, { tags: ['login', 'manual-login'] }, 'error')
    return thunkApi.rejectWithValue({ type: 'unknown', message, error })
  }
})
